<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import Icon from '@components/Icon.vue';

import { faSidebar } from '@icons/regular/faSidebar';

export interface FormStepConfig {
  title: string;
  subtitle?: string;
  autoProgress?: boolean;
  completed: boolean;
  invalid?: () => boolean;
}

export interface Props {
  steps: FormStepConfig[];
}

const props = defineProps<Props>();

const sidebarOpen = ref(true);

const currentStepIndex = ref(0);

const currentStep = computed(() => {
  return props.steps[currentStepIndex.value] as FormStepConfig;
});

const isCurrentStepCompleted = computed(() => {
  return currentStep.value.completed;
});

const isOnFinalStep = computed(() => {
  return currentStepIndex.value === props.steps.length - 1;
});

interface HeaderElement extends FormStepConfig {
  id: string;
  index: number;
  isClickable: boolean;
  isActive: boolean;
  isCompleted: boolean;
}

const headerElements = computed(() => {
  return props.steps.map((step, index) => {
    const id = `step-${index + 1}`;

    const isActive = index === currentStepIndex.value;
    const isCompleted = step.completed;

    let isClickable;

    if (index === 0) {
      isClickable = true;
    } else if (step.completed) {
      isClickable = true;
    } else {
      const previousStep = props.steps[index - 1];

      if (previousStep?.completed) {
        isClickable = true;
      } else {
        isClickable = false;
      }
    }

    return {
      ...step,
      id,
      index,
      isClickable,
      isActive,
      isCompleted,
    } as HeaderElement;
  });
});

// TODO: This probably could be done a little bit better...
const autoProgressionFlags: string[] = [];

watch(headerElements, (elements) => {
  for (const element of elements) {
    if (
      element.index === currentStepIndex.value &&
      element.completed &&
      element.autoProgress &&
      !autoProgressionFlags.includes(element.id)
    ) {
      autoProgressionFlags.push(element.id);

      nextStep();
    }
  }
});

function goToStep(element: HeaderElement) {
  if (!element.isClickable) return;

  currentStepIndex.value = element.index;
}

function nextStep() {
  if (currentStep.value.completed) {
    currentStepIndex.value++;
  }
}

function previousStep() {
  // go back back through steps sequentially until we find
  // the first isClickable step
  let index = currentStepIndex.value;

  while (index > 0) {
    index--;

    if (headerElements.value[index]?.isClickable) {
      currentStepIndex.value = index;
      break;
    }
  }
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value;
}

defineExpose({
  isCurrentStepCompleted,
  isOnFinalStep,
  nextStep,
  previousStep,
});
</script>

<template>
  <div class="step-form">
    <div class="d-flex align-items-start mr-2">
      <button
        v-b-tooltip.hover.bottom="sidebarOpen ? 'Collapse' : 'Expand'"
        class="sidebar-btn"
        @click="toggleSidebar"
      >
        <Icon :icon="faSidebar" />
      </button>
    </div>

    <div class="step-form-container">
      <div :class="['step-form-header', { 'sidebar-open': sidebarOpen }]">
        <div
          v-for="element in headerElements"
          :key="element.id"
          :class="[
            'form-step-nav-item',
            {
              active: element.isActive,
              completed: element.isCompleted,
              clickable: element.isClickable,
            },
          ]"
          @click="goToStep(element)"
        >
          <template v-if="sidebarOpen">
            <div class="step-title">
              <span class="step-title-number">{{ element.index + 1 }}</span>
              <span class="step-title-text">{{ element.title }}</span>
            </div>

            <div v-if="element.subtitle" class="step-subtitle">
              <span v-html="element.subtitle"></span>
            </div>
          </template>

          <template v-else>
            <div class="step-title">
              <span v-b-tooltip.right="element.title" class="step-title-number">
                {{ element.index + 1 }}
              </span>
            </div>
          </template>
        </div>
      </div>

      <div class="step-section-content">
        <slot :name="`step-${currentStepIndex + 1}`">
          <div>No content</div>
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.sidebar-btn {
  color: var(--text-primary);
  background-color: transparent;
  padding: 10px 14px;
  border-radius: 10px;
  transition: background-color 0.3s ease;

  &:hover {
    background: #ffffff0f;
  }
}

.step-form {
  display: flex;
  flex-direction: row;
}

.step-form-container {
  display: flex;
  flex: 1;
}

.step-form-header {
  width: 67px;
  padding-right: 20px;
  border-right: 1px solid var(--text-primary);
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  // transition: width 0.2s ease;

  &.sidebar-open {
    width: 300px;
  }
}

.form-step-nav-item {
  padding: 10px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease;

  &.clickable:hover {
    @include app-theme-light {
      background-color: #0000000d;
    }

    @include app-theme-dark {
      background-color: #ffffff0d;
    }
  }

  &:not(.clickable) {
    cursor: not-allowed;
  }

  &.active {
    background-color: #007bff !important;
    color: #fff !important;
  }
}

.step-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  opacity: 0.5;
  gap: 10px;
  text-wrap: nowrap;

  .form-step-nav-item.active &,
  .form-step-nav-item.completed &,
  .form-step-nav-item.clickable & {
    opacity: 1;
  }
}

.step-title-number {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid currentColor;
  font-size: 12px;
  font-weight: 600;
  padding: 12px;

  .form-step-nav-item.completed & {
    background-color: var(--success);
    border-color: var(--success);
    color: #004d3f;
  }
}

.step-subtitle {
  font-size: 12px;
  color: var(--text-secondary);
  margin-left: 35px;
  opacity: 0.2;

  .form-step-nav-item.active &,
  .form-step-nav-item.completed &,
  .form-step-nav-item.clickable & {
    opacity: 0.7;
  }
}

.step-section-content {
  flex: 1;
  padding: 20px;
}

.modal-form-section-content {
  margin: 0 !important;
}
</style>
